// Sign-in error surfacing. Yolutap has no passwords — the magic link is the
// only door — so an auth error that isn't shown reads as "nothing happened"
// and the visitor loops on the login button. Supabase returns these in the URL
// FRAGMENT (#error=…), which never reaches the server, so every consumer here
// is client-side by necessity.
//
// One dictionary serves both consumers: the React /auth/callback route and the
// static marketing landings (public/methodology*.html), which are served as raw
// HTML and never mount React — hence the hand-rolled snippet below.

export type AuthLang = "az" | "ru" | "en";

const LANGS: AuthLang[] = ["az", "ru", "en"];
export function normAuthLang(l: string | null | undefined): AuthLang {
  return LANGS.includes(l as AuthLang) ? (l as AuthLang) : "az";
}

type Texts = {
  otp_expired: string;
  access_denied: string;
  generic: string;
  // Session couldn't be established although no provider error came back —
  // in practice the link was opened on a different origin than it was
  // requested from (dev server vs prod), so the verifier is on the other one.
  no_session: string;
  cta: string;
  dismiss: string;
};

export const AUTH_ERR: Record<AuthLang, Texts> = {
  az: {
    otp_expired: "Keçidin vaxtı bitib. Yeni keçid istəyin.",
    access_denied: "Giriş təsdiqlənmədi. Yeni keçid istəyin.",
    generic: "Giriş alınmadı. Yeni keçid istəyin.",
    no_session:
      "Keçidi girişi istədiyiniz saytda açın — keçid başqa ünvanda açılıb. Əmin deyilsinizsə, buradan yeni keçid alın.",
    cta: "Yeni keçid al",
    dismiss: "Bağla",
  },
  ru: {
    otp_expired: "Срок ссылки истёк — запросите новую.",
    access_denied: "Вход не подтверждён. Запросите новую ссылку.",
    generic: "Не удалось войти. Запросите новую ссылку.",
    no_session:
      "Откройте ссылку на том же сайте, где запрашивали вход — она была открыта по другому адресу. Если не уверены, запросите новую ссылку здесь.",
    cta: "Запросить новую ссылку",
    dismiss: "Закрыть",
  },
  en: {
    otp_expired: "This link has expired — request a new one.",
    access_denied: "Sign-in was not confirmed. Request a new link.",
    generic: "Could not sign in. Request a new link.",
    no_session:
      "Open the link on the same site where you requested it — it was opened on a different address. If unsure, request a new link here.",
    cta: "Request a new link",
    dismiss: "Dismiss",
  },
};

// Supabase puts the machine-readable reason in error_code and a human (English,
// unlocalised) sentence in error_description. We key off the code and fall back
// to a visible generic — never to silence.
export function authErrorText(lang: AuthLang, code: string | null, error: string | null): string {
  const t = AUTH_ERR[lang];
  if (code === "otp_expired") return t.otp_expired;
  if (code === "access_denied" || error === "access_denied") return t.access_denied;
  return t.generic;
}

// Reads the error out of a URL's query OR fragment. Supabase uses the fragment
// for implicit-flow failures and the query for some provider errors.
export function readAuthError(href: string): { code: string | null; error: string | null } {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const code = url.searchParams.get("error_code") ?? hash.get("error_code");
  const error = url.searchParams.get("error") ?? hash.get("error");
  return { code, error };
}

// ─── static-landing injection (serve-time) ───────────────────────────────────
//
// Same shape as landingAnalyticsHtml(): the {index,az,en} routes splice this
// into the page's <!--ANALYTICS--> slot, so the three public/methodology*.html
// files stay untouched. Styles are inline because the static landings never
// load Tailwind — the values are the D1 tokens by hand.
export function authErrorBannerHtml(lang: AuthLang): string {
  const t = AUTH_ERR[lang];
  const msgs = JSON.stringify({
    otp_expired: t.otp_expired,
    access_denied: t.access_denied,
    generic: t.generic,
  });
  const cta = JSON.stringify(t.cta);
  const dismiss = JSON.stringify(t.dismiss);
  return (
    "<script>(function(){try{" +
    "var h=new URLSearchParams(location.hash.replace(/^#/,''));" +
    "var q=new URLSearchParams(location.search);" +
    "var code=q.get('error_code')||h.get('error_code');" +
    "var err=q.get('error')||h.get('error');" +
    "if(!code&&!err)return;" +
    "var M=" +
    msgs +
    ";var msg=M[code]||(err==='access_denied'?M.access_denied:M.generic);" +
    // Strip the noisy fragment so a reload doesn't replay the banner and the
    // address bar stops showing raw provider output.
    "try{history.replaceState(null,'',location.pathname+location.search);}catch(e){}" +
    "function mount(){" +
    "var w=document.createElement('div');" +
    "w.setAttribute('role','alert');" +
    "w.style.cssText='position:fixed;left:0;right:0;top:0;z-index:2147483000;padding:12px;background:#FBFAF7;border-bottom:1px solid #EEE5D8;font-family:\"Nunito Sans\",system-ui,sans-serif';" +
    "var b=document.createElement('div');" +
    "b.style.cssText='max-width:600px;margin:0 auto;border:1px solid rgba(197,48,48,.3);background:rgba(197,48,48,.08);border-radius:12px;padding:14px;display:flex;gap:10px;align-items:flex-start;flex-wrap:wrap';" +
    "var ic=document.createElement('span');ic.textContent='!';ic.setAttribute('aria-hidden','true');" +
    "ic.style.cssText='flex:0 0 auto;width:22px;height:22px;border-radius:50%;background:#9B2C2C;color:#fff;font-weight:700;font-size:15px;line-height:22px;text-align:center';" +
    // Message and, under it, the raw code — same shape as /auth/callback: the
    // sentence is ours and readable, the code is the machine's word and exists
    // so a screenshot tells support what happened. No code, no second line.
    "var w2=document.createElement('div');w2.style.cssText='flex:1 1 200px;min-width:0';" +
    "var p=document.createElement('p');p.textContent=msg;" +
    "p.style.cssText='margin:0;font-size:16px;line-height:1.35;color:#9B2C2C';" +
    "w2.appendChild(p);" +
    "var detail=code||err;" +
    "if(detail){var d=document.createElement('p');d.textContent=detail;" +
    "d.style.cssText='margin:6px 0 0;font-size:12px;line-height:1.3;word-break:break-all;color:rgba(15,42,56,.45)';" +
    "w2.appendChild(d);}" +
    "var a=document.createElement('a');a.href='/cabinet';a.textContent=" +
    cta +
    ";" +
    "a.style.cssText='flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 20px;border-radius:9999px;background:#F0A63A;color:#0F2A38;font-size:16px;font-weight:700;text-decoration:none';" +
    "var x=document.createElement('button');x.type='button';x.textContent='\\u00D7';x.setAttribute('aria-label'," +
    dismiss +
    ");" +
    "x.style.cssText='flex:0 0 auto;min-width:44px;min-height:44px;border:0;background:transparent;color:#9B2C2C;font-size:24px;line-height:1;cursor:pointer';" +
    "x.onclick=function(){w.remove();document.body.style.paddingTop=prev;};" +
    "b.appendChild(ic);b.appendChild(w2);b.appendChild(a);b.appendChild(x);w.appendChild(b);" +
    "var prev=document.body.style.paddingTop;" +
    "document.body.insertBefore(w,document.body.firstChild);" +
    "document.body.style.paddingTop=w.offsetHeight+'px';" +
    "}" +
    "if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',mount);else mount();" +
    "}catch(e){}})();</script>"
  );
}
